import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, FileText, ListChecks, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card, SectionTitle } from "@/components/Kpi";
import { GestioneCqModal } from "@/components/GestioneCqModal";
import { dateIt, eur, MESI } from "@/lib/format";

const GIORNI = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const oggiIso = () => iso(new Date());
const lunediDi = (d) => { const x = new Date(d); const g = (x.getDay() + 6) % 7; x.setDate(x.getDate() - g); x.setHours(0, 0, 0, 0); return x; };
const addGiorni = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
// fasce orarie della vista settimana: "" = senza orario, poi 09:00 … 18:00
const FASCE = ["", ...Array.from({ length: 10 }, (_, i) => `${String(9 + i).padStart(2, "0")}:00`)];
const fasciaDi = (ora) => {
  if (!ora) return "";
  const h = Math.min(18, Math.max(9, parseInt(ora.slice(0, 2), 10) || 9));
  return `${String(h).padStart(2, "0")}:00`;
};

function Evento({ r, onClick, compatto }) {
  const passato = !r.chiuso && r.data < oggiIso();
  return (
    <button type="button" onClick={onClick} data-testid={`cal-evento-${r.id}`} title={`${r.nominativo} · ${r.agenzia} · trascina per spostare`}
      draggable onDragStart={(e) => { e.dataTransfer.setData("text/cq-ricontatto", r.id); e.dataTransfer.effectAllowed = "move"; }}
      className={`w-full text-left rounded-md px-2 py-1 text-[11px] leading-tight border-l-[3px] transition-colors duration-150 cursor-grab active:cursor-grabbing
        ${r.chiuso ? "opacity-50 line-through" : ""} ${passato ? "ring-1 ring-[#C8102E]/50" : ""}`}
      style={{ borderLeftColor: r.colore, background: `${r.colore}14` }}>
      <span className="font-mono font-semibold" style={{ color: r.colore }}>{r.ora || "--:--"}</span>{" "}
      <span className="font-semibold text-slate-800">{r.nominativo}</span>
      {!compatto && <div className="text-slate-500 truncate">{r.agenzia} · {r.stato}</div>}
    </button>
  );
}

function Appunti({ r, onChiudi, onApri }) {
  const p = r.preventivo || {};
  const righe = [["Importo richiesto", p.importo != null && eur(p.importo)], ["Durata", p.durata && `${p.durata} rate`],
    ["Rata", p.rata != null && eur(p.rata)], ["TAN", p.tan != null && `${p.tan}%`], ["TAEG", p.taeg != null && `${p.taeg}%`],
    ["Estinzione", p.importo_estinzione != null && eur(p.importo_estinzione)], ["Note", p.note]].filter(([, v]) => v);
  return (
    <Card className="p-5 sticky top-4" data-testid="cal-appunti">
      <div className="flex items-start justify-between gap-2">
        <div>
          <span className="inline-block text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-0.5 rounded" style={{ color: r.colore, background: `${r.colore}14` }}>{r.tipo_label}</span>
          <h3 className="text-lg font-bold text-slate-900 mt-2">{r.nominativo}</h3>
          <div className="text-sm text-slate-500">{r.agenzia}{r.numero_pratica ? ` · pratica ${r.numero_pratica}` : ""} · {r.stato}</div>
          <div className="text-sm font-mono text-slate-700 mt-1">Ricontatto {dateIt(r.data)}{r.ora ? ` alle ${r.ora}` : ""}</div>
        </div>
        <button type="button" onClick={onChiudi} className="text-slate-400 hover:text-slate-700" data-testid="cal-appunti-chiudi"><X size={16} /></button>
      </div>
      {r.esito_chiamata && <p className="mt-3 text-sm text-slate-700"><b>Ultimo esito:</b> {r.esito_chiamata}</p>}
      {r.ultima_nota && <p className="mt-1 text-sm text-slate-600 whitespace-pre-wrap"><b>Ultima nota:</b> {r.ultima_nota}</p>}
      <div className="mt-4">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1.5"><FileText size={12} /> Preventivo proposto</div>
        {righe.length ? (
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
            {righe.map(([k, v]) => <><dt key={`${k}-k`} className="text-slate-500">{k}</dt><dd key={`${k}-v`} className="font-mono text-slate-800 text-right">{v}</dd></>)}
          </dl>
        ) : <div className="text-sm text-slate-400">Nessun preventivo inserito.</div>}
      </div>
      <div className="mt-4">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-1.5"><ListChecks size={12} /> Documenti mancanti</div>
        {r.documenti_mancanti?.length ? (
          <ul className="text-sm text-slate-800 space-y-0.5">{r.documenti_mancanti.map((d) => <li key={d} className="flex gap-2"><span className="text-[#C8102E]">•</span>{d}</li>)}</ul>
        ) : <div className="text-sm text-slate-400">Nessun documento mancante.</div>}
      </div>
      <button type="button" onClick={onApri} data-testid="cal-appunti-apri"
        className="mt-5 w-full px-4 py-2 rounded-md bg-[#0018A8] text-white text-sm font-semibold hover:bg-[#001480]">Apri scheda e aggiorna</button>
    </Card>
  );
}

/** Calendario ricontatti CQ: settimana (5 colonne) o mese, colore per sezione di provenienza. */
export default function CalendarioCq() {
  const [vista, setVista] = useState("settimana");
  const [rif, setRif] = useState(() => new Date());
  const [dati, setDati] = useState({ rows: [], tipi: {} });
  const [sel, setSel] = useState(null);
  const [modal, setModal] = useState(null);
  const [meta, setMeta] = useState(null);
  const [over, setOver] = useState(null);

  // ora: undefined = solo cambio giorno (vista mese); "" = senza orario; "HH:00" = fascia oraria
  const dropSu = (giorno, ora) => async (e) => {
    e.preventDefault();
    setOver(null);
    const id = e.dataTransfer.getData("text/cq-ricontatto");
    if (!id) return;
    const r = dati.rows.find((x) => x.id === id);
    if (!r) return;
    const cambiaOra = ora !== undefined;
    const nuovaOra = cambiaOra ? (ora || null) : r.ora;
    if (r.data === giorno && (!cambiaOra || nuovaOra === r.ora)) return;
    setDati((d) => ({ ...d, rows: d.rows.map((x) => (x.id === id ? { ...x, data: giorno, ora: nuovaOra } : x)) }));
    try {
      await api.put(`/cq/ricontatti/${id}/sposta`, { data_ricontatto: giorno, ...(cambiaOra ? { ora_ricontatto: ora } : {}) });
      toast.success(`${r.nominativo}: ricontatto spostato al ${dateIt(giorno)}${nuovaOra ? ` alle ${nuovaOra}` : ""}`);
      setSel((s) => (s?.id === id ? { ...s, data: giorno, ora: nuovaOra } : s));
    } catch (err) { toast.error(apiError(err)); carica(); }
  };
  const dragProps = (giorno, ora) => {
    const k = ora === undefined ? giorno : `${giorno}|${ora}`;
    return {
      onDragOver: (e) => { if (e.dataTransfer.types.includes("text/cq-ricontatto")) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (over !== k) setOver(k); } },
      onDragLeave: () => setOver((o) => (o === k ? null : o)),
      onDrop: dropSu(giorno, ora),
    };
  };

  const range = useMemo(() => {
    if (vista === "settimana") { const l = lunediDi(rif); return { dal: iso(l), al: iso(addGiorni(l, 4)), lunedi: l }; }
    const primo = new Date(rif.getFullYear(), rif.getMonth(), 1), ultimo = new Date(rif.getFullYear(), rif.getMonth() + 1, 0);
    return { dal: iso(primo), al: iso(ultimo), primo, ultimo };
  }, [vista, rif]);

  const carica = useCallback(() => {
    api.get(`/cq/ricontatti?dal=${range.dal}&al=${range.al}`).then((r) => setDati(r.data)).catch((e) => toast.error(apiError(e)));
  }, [range]);
  useEffect(() => { carica(); }, [carica]);
  useEffect(() => { api.get("/cq/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const perGiorno = useMemo(() => dati.rows.reduce((a, r) => { (a[r.data] ||= []).push(r); return a; }, {}), [dati]);
  const sposta = (n) => setRif((d) => vista === "settimana" ? addGiorni(d, 7 * n) : new Date(d.getFullYear(), d.getMonth() + n, 1));
  const apriScheda = async (r) => {
    try { const c = (await api.get(`/cq/${r.tipo}/${r.id}`)).data; setModal(c); }
    catch (e) { toast.error(apiError(e)); }
  };
  const titolo = vista === "settimana"
    ? `Settimana ${dateIt(range.dal)} – ${dateIt(range.al)}`
    : `${MESI[rif.getMonth()]} ${rif.getFullYear()}`;

  return (
    <div data-testid="calendario-cq">
      <SectionTitle sub="Ricontatti fissati nelle schede CQ · un colore per sezione di provenienza · clicca un ricontatto per gli appunti, trascinalo su un altro giorno per spostarlo"
        right={
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-slate-200 bg-white overflow-hidden" data-testid="cal-switch-vista">
              {[["settimana", "Settimana"], ["mese", "Mese"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setVista(k)} data-testid={`cal-vista-${k}`}
                  className={`px-4 py-2 text-sm font-semibold transition-colors duration-150 ${vista === k ? "bg-[#0018A8] text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>
              ))}
            </div>
            <button type="button" onClick={() => sposta(-1)} data-testid="cal-prec" className="h-9 w-9 grid place-items-center rounded-md border border-slate-200 bg-white hover:border-[#0018A8]"><ChevronLeft size={16} /></button>
            <button type="button" onClick={() => setRif(new Date())} data-testid="cal-oggi" className="px-3 h-9 rounded-md border border-slate-200 bg-white text-sm hover:border-[#0018A8]">Oggi</button>
            <button type="button" onClick={() => sposta(1)} data-testid="cal-succ" className="h-9 w-9 grid place-items-center rounded-md border border-slate-200 bg-white hover:border-[#0018A8]"><ChevronRight size={16} /></button>
          </div>
        }>
        Calendario
      </SectionTitle>

      <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-slate-600" data-testid="cal-legenda">
        <span className="font-semibold text-slate-800 text-sm">{titolo}</span>
        {Object.entries(dati.tipi || {}).map(([k, t]) => (
          <span key={k} className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm" style={{ background: t.colore }} /> {t.label}</span>
        ))}
        <span className="text-slate-400">· {dati.rows.length} ricontatti nel periodo</span>
      </div>

      <div className={`grid gap-6 ${sel ? "xl:grid-cols-[1fr_360px]" : ""}`}>
        {vista === "settimana" ? (
          <Card className="p-3 overflow-x-auto" data-testid="cal-settimana">
            <div className="grid grid-cols-[56px_repeat(5,minmax(0,1fr))] gap-x-2">
              <div />
              {[0, 1, 2, 3, 4].map((i) => {
                const d = addGiorni(range.lunedi, i), k = iso(d), oggi = k === oggiIso();
                return (
                  <div key={k} data-testid={`cal-giorno-${k}`} className={`flex items-baseline justify-between px-2 pb-2 border-b-2 ${oggi ? "border-[#0018A8]" : "border-slate-200"}`}>
                    <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">{GIORNI[i]}</span>
                    <span className={`font-mono text-lg font-bold ${oggi ? "text-[#0018A8]" : "text-slate-800"}`}>{d.getDate()}</span>
                  </div>
                );
              })}
              {FASCE.map((fascia) => {
                const senza = fascia === "";
                return [
                  <div key={`h-${fascia}`} className={`pt-1.5 pr-1 text-right font-mono text-[11px] ${senza ? "text-slate-400 leading-tight" : "text-slate-500"}`}
                    data-testid={`cal-fascia-${senza ? "senza" : fascia.slice(0, 2)}`}>
                    {senza ? "Senza orario" : fascia}
                  </div>,
                  ...[0, 1, 2, 3, 4].map((i) => {
                    const k = iso(addGiorni(range.lunedi, i)), oggi = k === oggiIso();
                    const ev = (perGiorno[k] || []).filter((r) => fasciaDi(r.ora) === fascia);
                    const overK = over === `${k}|${fascia}`;
                    return (
                      <div key={`${k}|${fascia}`} {...dragProps(k, fascia)} data-testid={`cal-cella-${k}-${senza ? "senza" : fascia.slice(0, 2)}`}
                        className={`min-h-[52px] border-b border-slate-100 px-1 py-1 space-y-1 transition-colors duration-150
                          ${senza ? "bg-slate-50/70" : ""} ${oggi ? "bg-blue-50/30" : ""} ${overK ? "!bg-blue-50 ring-2 ring-inset ring-dashed ring-[#0018A8] rounded-md" : ""}`}>
                        {ev.map((r) => <Evento key={r.id} r={r} onClick={() => setSel(r)} />)}
                      </div>
                    );
                  }),
                ];
              })}
            </div>
          </Card>
        ) : (
          <Card className="p-3" data-testid="cal-mese">
            <div className="grid grid-cols-7 gap-1 mb-1">
              {GIORNI.map((g) => <div key={g} className="text-center text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500 py-1">{g}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {(() => {
                const celle = [];
                const offset = (range.primo.getDay() + 6) % 7;
                for (let i = 0; i < offset; i += 1) celle.push(<div key={`v${i}`} className="min-h-[104px] rounded-md bg-slate-50/60" />);
                for (let g = 1; g <= range.ultimo.getDate(); g += 1) {
                  const d = new Date(rif.getFullYear(), rif.getMonth(), g), k = iso(d), oggi = k === oggiIso(), weekend = d.getDay() === 0 || d.getDay() === 6;
                  const ev = perGiorno[k] || [];
                  celle.push(
                    <div key={k} data-testid={`cal-giorno-${k}`} {...dragProps(k)}
                      className={`min-h-[104px] rounded-md border p-1.5 transition-colors duration-150 ${oggi ? "border-[#0018A8] ring-1 ring-[#0018A8]" : "border-slate-200"} ${weekend ? "bg-slate-50" : "bg-white"} ${over === k ? "!bg-blue-50 ring-2 ring-dashed ring-[#0018A8]" : ""}`}>
                      <div className={`font-mono text-sm font-bold text-right ${oggi ? "text-[#0018A8]" : "text-slate-700"}`}>{g}</div>
                      <div className="space-y-1 mt-0.5">
                        {ev.slice(0, 3).map((r) => <Evento key={r.id} r={r} onClick={() => setSel(r)} compatto />)}
                        {ev.length > 3 && (
                          <button type="button" onClick={() => { setVista("settimana"); setRif(d); }} className="text-[11px] text-[#0018A8] hover:underline pl-1">
                            +{ev.length - 3} altri
                          </button>
                        )}
                      </div>
                    </div>,
                  );
                }
                return celle;
              })()}
            </div>
          </Card>
        )}
        {sel && <Appunti r={sel} onChiudi={() => setSel(null)} onApri={() => apriScheda(sel)} />}
      </div>

      <GestioneCqModal open={!!modal} meta={meta} contatto={modal} onClose={() => setModal(null)}
        onSaved={() => { carica(); setSel(null); }} />
    </div>
  );
}
